import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import styled from "@emotion/styled";
import IResult from "../../models/result/IResult";
import {
    ReferenceKind,
    ResolvedReferenceVariable
} from "../../utils/referenceVariables";

interface ReferenceVariableEditorModalProps {
    reference: ResolvedReferenceVariable;
    kind: ReferenceKind;
    onClose: () => void;
    onSave: (
        reference: ResolvedReferenceVariable,
        value: string
    ) => Promise<IResult<ResolvedReferenceVariable>>;
}

const capitalize = (value: string): string => (
    value.length ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value
);

const getReferenceLabel = (
    reference: ResolvedReferenceVariable,
    kind: ReferenceKind
): string => `${(reference.scope || "Referenced").toLowerCase()} ${kind}`;

const ReferenceVariableEditorModal: React.FC<ReferenceVariableEditorModalProps> = ({
    reference,
    kind,
    onClose,
    onSave
}) => {
    const [value, setValue] = useState(reference.value || "");
    const [error, setError] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const referenceLabel = getReferenceLabel(reference, kind);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isSaving) {
                onClose();
            }
        };
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [isSaving, onClose]);

    const handleSave = async () => {
        if (isSaving || value === reference.value) {
            return;
        }

        setError("");
        setIsSaving(true);
        try {
            const result = await onSave(reference, value);
            if (result.resultType === "Ok") {
                onClose();
                return;
            }

            setError(result.errors?.[0] || `Could not update the ${kind}`);
        } catch (_error) {
            setError(`Could not update the ${kind}`);
        }
        setIsSaving(false);
    };

    return ReactDOM.createPortal(
        <ModalOverlay
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !isSaving) {
                    onClose();
                }
            }}
        >
            <Modal
                role="dialog"
                aria-modal="true"
                aria-labelledby="reference-variable-editor-title"
            >
                <ModalHeader>
                    <ModalTitle id="reference-variable-editor-title">
                        Edit {referenceLabel}
                    </ModalTitle>
                    <ModalReferenceName>
                        {reference.name}
                    </ModalReferenceName>
                </ModalHeader>
                <ValueInput
                    autoFocus
                    aria-label={`${capitalize(reference.scope || "Referenced")} ${kind} value`}
                    value={value}
                    onChange={(event) => {
                        setValue(event.target.value);
                        setError("");
                    }}
                    disabled={isSaving}
                />
                {error && <ModalError role="alert">{error}</ModalError>}
                <ModalActions>
                    <CancelButton
                        type="button"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </CancelButton>
                    <SaveButton
                        type="button"
                        onClick={() => { void handleSave(); }}
                        disabled={isSaving || value === reference.value}
                    >
                        {isSaving ? "Saving..." : `Save ${referenceLabel}`}
                    </SaveButton>
                </ModalActions>
            </Modal>
        </ModalOverlay>,
        document.body
    );
};

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 0;
    background: rgba(16, 40, 61, 0.5);
`;

const Modal = styled.div`
    width: 100%;
    height: 92vh;
    height: 92dvh;
    max-height: 960px;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 20px 16px max(16px, env(safe-area-inset-bottom));
    border-radius: 8px 8px 0 0;
    background: #ffffff;
    box-shadow: 0 -18px 48px rgba(16, 40, 61, 0.22);

    @media (min-width: 600px) {
        width: min(960px, calc(100vw - 48px));
        height: min(84vh, 900px);
        height: min(84dvh, 900px);
        margin: auto;
        padding: 24px;
        border-radius: 8px;
        box-shadow: 0 24px 64px rgba(16, 40, 61, 0.28);
    }
`;

const ModalHeader = styled.header`
    min-width: 0;
    display: grid;
    gap: 4px;
`;

const ModalTitle = styled.h2`
    min-width: 0;
    margin: 0;
    color: #10283d;
    font-size: 20px;
    font-weight: 900;
    line-height: 1.3;
    overflow-wrap: anywhere;
`;

const ModalReferenceName = styled.div`
    min-width: 0;
    color: #6f8297;
    font-family: 'Courier New', monospace;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
    overflow-wrap: anywhere;
`;

const ValueInput = styled.textarea`
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    resize: none;
    border: 1px solid #d8e0e7;
    border-radius: 8px;
    padding: 14px;
    background: #ffffff;
    color: #1d3147;
    font-family: Muli, sans-serif;
    font-size: 15px;
    line-height: 1.55;

    &:focus {
        outline: none;
        border-color: #31b6d3;
        box-shadow: 0 0 0 3px rgba(49, 182, 211, 0.12);
    }
`;

const ModalError = styled.div`
    padding: 10px 12px;
    border-radius: 8px;
    background: rgba(176, 75, 56, 0.08);
    color: #9d3f2f;
    font-size: 13px;
    font-weight: 800;
`;

const ModalActions = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr);
    gap: 10px;
`;

const CancelButton = styled.button`
    min-height: 46px;
    border: 1px solid #d8e0e7;
    border-radius: 8px;
    background: #ffffff;
    color: #314b65;
    font-family: Muli, sans-serif;
    font-size: 14px;
    font-weight: 900;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
        opacity: 0.58;
    }
`;

const SaveButton = styled(CancelButton)`
    border-color: #1d3147;
    background: #1d3147;
    color: #ffffff;
`;

export default ReferenceVariableEditorModal;
